import React from 'react'

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  InMemoryCache,
} from '@apollo/client'
import {ErrorResponse, onError} from '@apollo/client/link/error'
import {createUploadLink} from 'apollo-upload-client'

import {
  SESSION_EXPIRED_EVENT,
  UNAUTHENTICATED_ROUTE_PREFIXES,
} from '@progyny/const/sessionExpiredEvent'
import {REACT_APP_BACKEND_URL} from '@progyny/utils/env'

type GraphQLWrapperProps = {
  children: React.ReactNode
}

if (!REACT_APP_BACKEND_URL) {
  throw new Error('Backend URL is missing form env vars!')
}

const BACKEND_URL =
  document.location.hostname === 'member.apryl.co'
    ? 'https://backend.apryl.co/employees/graphql'
    : `${REACT_APP_BACKEND_URL}/graphql`

const errorHandler = onError(({graphQLErrors, networkError}: ErrorResponse) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({message, locations, path}) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    )

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)

    if ('statusCode' in networkError && networkError.statusCode === 401) {
      const isOnUnauthenticatedRoute = UNAUTHENTICATED_ROUTE_PREFIXES.some(
        (prefix) => window.location.pathname.startsWith(prefix)
      )
      if (!isOnUnauthenticatedRoute) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
      }
    }
  }
})

// If typescript gives an error here, make sure @apollo/client package is
// still aligned. We have a "resolutions" section in package.json to achieve
// this, because @types/apollo-client-link pulls in ^3.0.0, which differs
// in ApolloLink structure from our ~3.3 client.
const apiLink: ApolloLink = createUploadLink({
  uri: BACKEND_URL,
  credentials: 'include',
})

const client = new ApolloClient({
  link: from([errorHandler, apiLink]),
  cache: new InMemoryCache({addTypename: false}),
})

const GraphqlWrapper = ({children}: GraphQLWrapperProps) => {
  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export default GraphqlWrapper
